import { useMemo, useState } from "react";
import { fetchMediaBlob, mediaUrl } from "../api";
import { humanError } from "../lib/errors";
import { FRAMES, MEDIA_RULES, needsCrop } from "../lib/frames";
import { accountLabel, type LibraryItem, type SocialAccount, type SocialGroup, type SocialMedia } from "../social";
import ConfirmRemove from "./ConfirmRemove";
import { DateField, SelectField, TimeField } from "./fields";
import { IconClock } from "./icons";
import MediaCropper from "./MediaCropper";
import MediaLibrary from "./MediaLibrary";
import PostPreview from "./PostPreview";

type Net = "facebook" | "instagram";

type Props = {
  day: string;
  accounts: SocialAccount[];
  groups: SocialGroup[];
  library: LibraryItem[];
  busy: boolean;
  onClose: () => void;
  onError: (msg: string) => void;
  onUpload: (file: File) => Promise<LibraryItem>;
  onDeleteLibrary: (id: string) => Promise<void>;
  onSave: (payload: {
    caption: string;
    accounts: string[];
    media: SocialMedia[];
    when: "now" | "schedule";
    scheduled_at: string;
  }) => Promise<void>;
};

function showUrl(item: LibraryItem | SocialMedia): string {
  if ("preview" in item && item.preview) return mediaUrl(item.preview);
  if ("thumb" in item && item.thumb) return mediaUrl(item.thumb);
  return mediaUrl(item.url);
}

export default function PublishComposer({
  day,
  accounts,
  groups,
  library,
  busy,
  onClose,
  onError,
  onUpload,
  onDeleteLibrary,
  onSave,
}: Props) {
  const [caption, setCaption] = useState("");
  const firstTarget = groups[0] ? `g:${groups[0].id}` : accounts[0] ? `a:${accounts[0].id}` : "";
  const [target, setTarget] = useState(firstTarget);
  const [frameId, setFrameId] = useState("1:1");
  const today = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`;
  const [when, setWhen] = useState<"now" | "schedule">(day > today ? "schedule" : "now");
  const [time, setTime] = useState("09:00");
  const [date, setDate] = useState(day);
  const [media, setMedia] = useState<SocialMedia[]>([]);
  const [crop, setCrop] = useState<{ src: string; name: string } | null>(null);
  const [previewNet, setPreviewNet] = useState<Net>("facebook");
  const [libOpen, setLibOpen] = useState(false);
  const frame = FRAMES.find((f) => f.id === frameId) || FRAMES[0];
  const picked = useMemo(() => {
    if (target.startsWith("g:")) {
      return groups.find((g) => g.id === target.slice(2))?.accounts || [];
    }
    if (target.startsWith("a:")) return [target.slice(2)];
    return [];
  }, [target, groups]);
  const chosen = useMemo(() => accounts.filter((a) => picked.includes(a.id)), [accounts, picked]);
  const fb = chosen.find((a) => a.network === "facebook");
  const ig = chosen.find((a) => a.network === "instagram");
  const targetOptions = [
    ...groups.map((g) => ({ value: `g:${g.id}`, label: `Groupe · ${g.name}` })),
    ...accounts.map((a) => ({ value: `a:${a.id}`, label: accountLabel(a) })),
  ];

  function addItem(item: LibraryItem, preview?: string) {
    setMedia((prev) => [
      ...prev,
      { id: item.id, kind: item.kind, url: item.url, preview: preview || item.thumb, name: item.name, frame: frame.id },
    ]);
  }

  function inspectImage(src: string, onOk: () => void, onNeedCrop: () => void) {
    const img = new Image();
    img.onload = () => {
      if (needsCrop(img.naturalWidth, img.naturalHeight, frame)) onNeedCrop();
      else onOk();
    };
    img.onerror = onOk;
    img.src = src;
  }

  async function storeFile(file: File): Promise<LibraryItem | null> {
    try {
      return await onUpload(file);
    } catch (err) {
      onError(humanError(err));
      return null;
    }
  }

  async function takeFile(file: File) {
    const localUrl = URL.createObjectURL(file);
    if (file.type.startsWith("video/")) {
      const item = await storeFile(file);
      if (item) addItem(item, localUrl);
      return;
    }
    inspectImage(
      localUrl,
      () => {
        void storeFile(file).then((item) => {
          if (item) addItem(item, localUrl);
        });
      },
      () => setCrop({ src: localUrl, name: file.name }),
    );
  }

  function takeLibrary(item: LibraryItem) {
    if (item.kind === "video") {
      addItem(item);
      return;
    }
    void fetchMediaBlob(item.url)
      .then((src) => {
        inspectImage(src, () => addItem(item, src), () => setCrop({ src, name: item.name }));
      })
      .catch(() => addItem(item));
  }

  async function acceptCrop(blob: Blob) {
    const localUrl = URL.createObjectURL(blob);
    setCrop(null);
    const file = new File([blob], crop?.name || "visuel.jpg", { type: "image/jpeg" });
    const item = await storeFile(file);
    if (item) addItem(item, localUrl);
  }

  function submit() {
    void onSave({
      caption,
      accounts: picked,
      media: media.map(({ preview: _p, ...row }) => row),
      when,
      scheduled_at: `${date}T${time}:00`,
    });
  }

  return (
    <>
      {crop ? (
          <MediaCropper src={crop.src} frame={frame} onCancel={() => setCrop(null)} onDone={(b) => void acceptCrop(b)} />
        ) : (
          <section className="pr-card p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold">Nouveau post</h2>
                <p className="mt-1 text-sm text-slate-500">Choisissez une Page ou un groupe.</p>
              </div>
              <button type="button" className="pr-btn-ghost" onClick={onClose}>
                Fermer
              </button>
            </div>

            <div className="mt-5 grid gap-6 md:grid-cols-2">
              <div className="space-y-5">
                <div>
                  <p className="mb-2 text-xs text-slate-500">Publier sur</p>
                  {targetOptions.length === 0 ? (
                    <p className="text-sm text-slate-500">Reliez d’abord une Page dans Destinations.</p>
                  ) : (
                    <SelectField value={target} onChange={setTarget} options={targetOptions} />
                  )}
                </div>

                <label className="block text-xs text-slate-500">
                  Texte
                  <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={4} className="pr-area mt-1" placeholder="Écrivez le post…" />
                  <span className="mt-1 block text-[11px] text-slate-400">{caption.length}/2200</span>
                </label>

                <div>
                  <p className="mb-2 text-xs text-slate-500">Format du visuel</p>
                  <SelectField value={frameId} onChange={setFrameId} options={FRAMES.map((f) => ({ value: f.id, label: f.label }))} />
                  <ul className="mt-2 space-y-1 text-[11px] leading-4 text-slate-500">
                    {MEDIA_RULES.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-wrap gap-2">
                  <label className="pr-btn cursor-pointer">
                    Téléverser
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                      className="sr-only"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void takeFile(file);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  <button type="button" className="pr-btn-ghost" onClick={() => setLibOpen(true)}>
                    Médiathèque
                  </button>
                </div>

                {media.length > 0 ? (
                  <ul className="flex flex-wrap gap-2">
                    {media.map((m) => (
                      <li key={m.id} className="flex flex-col items-start gap-1">
                        {m.kind === "image" ? (
                          <img src={showUrl(m)} alt="" className="h-16 w-16 rounded-xl object-cover" />
                        ) : (
                          <span className="flex h-16 w-16 items-center justify-center rounded-xl bg-mist text-[10px]">Vidéo</span>
                        )}
                        <ConfirmRemove question="Retirer ce visuel ?" onConfirm={() => setMedia((prev) => prev.filter((x) => x.id !== m.id))} />
                      </li>
                    ))}
                  </ul>
                ) : null}

                {when === "schedule" ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <p className="mb-1 text-xs text-slate-500">Jour</p>
                      <DateField value={date} onChange={setDate} />
                    </div>
                    <div>
                      <p className="mb-1 text-xs text-slate-500">Heure</p>
                      <TimeField value={time} onChange={setTime} />
                    </div>
                  </div>
                ) : null}

                {ig && media.length === 0 ? <p className="text-xs text-amber-800">Instagram demande un visuel (photo ou vidéo).</p> : null}

                <div className="pr-split">
                  <button type="button" disabled={busy} className="pr-btn" onClick={submit}>
                    {busy ? "Enregistrement…" : when === "schedule" ? "Planifier" : "Envoyer"}
                  </button>
                  <button
                    type="button"
                    className={`pr-split-clock ${when === "schedule" ? "is-on" : ""}`}
                    aria-label="Planifier"
                    title="Planifier"
                    onClick={() => setWhen((v) => (v === "schedule" ? "now" : "schedule"))}
                  >
                    <IconClock className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <aside className="md:sticky md:top-0">
                <div className="mb-3 flex gap-2">
                  <button type="button" onClick={() => setPreviewNet("facebook")} className={`pr-chip ${previewNet === "facebook" ? "is-on" : "is-off"}`}>
                    Facebook
                  </button>
                  <button type="button" onClick={() => setPreviewNet("instagram")} className={`pr-chip ${previewNet === "instagram" ? "is-on" : "is-off"}`}>
                    Instagram
                  </button>
                </div>
                {previewNet === "facebook" ? (
                  <PostPreview network="facebook" name={fb?.name || "Facebook"} caption={caption} media={media} frameId={frameId} on={Boolean(fb)} />
                ) : (
                  <PostPreview network="instagram" name={ig?.name || "Instagram"} caption={caption} media={media} frameId={frameId} on={Boolean(ig)} />
                )}
              </aside>
            </div>
          </section>
        )}
      {libOpen && !crop ? (
        <MediaLibrary
          items={library}
          onClose={() => setLibOpen(false)}
          onUpload={onUpload}
          onDelete={async (id) => {
            await onDeleteLibrary(id);
            setMedia((prev) => prev.filter((x) => x.id !== id));
          }}
          onPick={(item) => {
            takeLibrary(item);
            setLibOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
